import React, { useState, useContext } from "react";
import CustomDropdown from "../../../../components/CustomDropdown";
import { AddUserSchema } from "../../../../components/Helpers";
import Hoc from "../../../../components/HOC";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import TextInput from "../../../../components/TextInput";
import { MdArrowBack } from "react-icons/md";
import { FaMapMarkerAlt, FaEye, FaEyeSlash } from "react-icons/fa";
import { FiUsers, FiUser } from "react-icons/fi";
import { UserContext } from "../../../../Context/User";

const AddEmployeeForm = () => {
  const navigate = useNavigate();
  const { addUser, addUserLoading, addUserError, addUserSuccess, resetUserState } = useContext(UserContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const initialValues = {
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    gender: "M",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "USA",
    zip: "",
    acctType: "ADMIN",
    stsCd: "A",
  };

  const countryOptions = [
    { value: "USA", label: "USA" },
    { value: "IND", label: "IND" },
  ];

  const handleAddEmployee = async (newEmployee) => {
    setIsSubmitting(true);
    
    try {
      const payload = {
        email: newEmployee.email,
        password: newEmployee.password,
        firstName: newEmployee.firstName,
        lastName: newEmployee.lastName,
        gender: newEmployee.gender,
        phone: newEmployee.phone,
        addressLine1: newEmployee.addressLine1,
        addressLine2: newEmployee.addressLine2,
        city: newEmployee.city,
        state: newEmployee.state,
        country: newEmployee.country,
        zip: newEmployee.zip,
        acctType: newEmployee.acctType,
        stsCd: newEmployee.stsCd
      };

      const response = await addUser(payload);

      if (!response) {
        toast.error("Failed to create user: No response from server");
        return;
      }

      const isSuccess = 
        response.success === true || 
        response.status === 200 || 
        response.status === 201 || 
        (response.data && (response.data.success === true || response.data.status === 200 || response.data.status === 201)) ||
        response === "User created successfully" ||
        response?.data === "User created successfully";

      if (isSuccess) {
        toast.success("User created successfully");
        setTimeout(() => {
        navigate("/users");
        }, 1000);
        return;
      }

      const errorMessage = 
        response.message || 
        response.error || 
        response.data?.message || 
        response.data?.error || 
        (typeof response === 'string' ? response : null) ||
        "Failed to create user";
        toast.error(errorMessage);

    } catch (error) {
      
      if (error?.response?.status === 200 || error?.response?.status === 201) {
        toast.success("User created successfully");
        setTimeout(() => {
          navigate("/users");
        }, 1000);
        return;
      }

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        error?.message ||
        (typeof error === 'string' ? error : null) ||
        "Failed to create user";
      toast.error(backendMessage);
    } finally {
      setIsSubmitting(false);
      resetUserState();
    }
  };

  return (
    <div className="w-full h-full bg-white p-6">
      
      <Formik
        initialValues={initialValues}
        validationSchema={AddUserSchema}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={async(values) => {
          const formattedPhone = values.phone ? values.phone.replace(/\D/g, "") : "";
          
          await handleAddEmployee({
            email: values.email.trim(),
            password: values.password,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            gender: values.gender,
            phone: formattedPhone,
            addressLine1: values.addressLine1.trim(),
            addressLine2: values.addressLine2.trim(),
            city: values.city.trim(),
            state: values.state.trim(),
            country: values.country,
            zip: values.zip.trim(),
            acctType: values.acctType,
            stsCd: values.stsCd,
          });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
        }) => (
          <Form className="space-y-4">
            {/* Account Information Section */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 mb-4">
              <div className="flex items-center justify-between"> </div>
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2 mb-3">
                <FiUser size={18} className="text-[#201F1E]" /> 
                Account Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <TextInput
                    value={values.email}
                    name="email"
                    placeholder="Email"
                    handleChange={handleChange}
                    legend
                    required
                  />
                  {errors.email && touched.email && (
                    <div className="text-red-500 text-sm mt-1">{errors.email}</div>
                  )}
                </div>
                <div>
                  <div className="relative">
                    <TextInput
                      value={values.password}
                      name="password"
                      placeholder="Password"
                      handleChange={handleChange}
                      type={showPassword ? "text" : "password"}
                      legend
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ transform: 'translateY(-50%)', zIndex: 10 }}
                    >
                      {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <div className="text-red-500 text-sm mt-1">{errors.password}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 mb-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2 mb-3">
                <FiUsers size={18} className="text-[#201F1E]" /> 
                Personal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <TextInput
                    value={values.firstName}
                    name="firstName"
                    placeholder="First Name"
                    handleChange={handleChange}
                    legend
                    required
                  />
                  {errors.firstName && touched.firstName && (
                    <div className="text-red-500 text-sm mt-1">{errors.firstName}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.lastName}
                    name="lastName"
                    placeholder="Last Name"
                    handleChange={handleChange}
                    legend
                    required
                  />
                  {errors.lastName && touched.lastName && (
                    <div className="text-red-500 text-sm mt-1">{errors.lastName}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.phone}
                    name="phone"
                    placeholder="Phone Number"
                    type="tel"
                    handleChange={e => {
                      const allowed = e.target.value.replace(/[^\d]/g, "").slice(0, 13);
                      setFieldValue("phone", allowed);
                    }}
                    legend
                    maxLength={13}
                    required
                  />
                  {errors.phone && (
                    <div className="text-red-500 text-sm mt-1">{errors.phone}</div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2">
                      <Field type="radio" name="gender" value="M" id="gender-male" checked={values.gender === "M"} className="text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700">Male</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <Field type="radio" name="gender" value="F" id="gender-female" checked={values.gender === "F"} className="text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700">Female</span>
                    </label>
                  </div>
                  {errors.gender && touched.gender && (
                    <div className="text-red-500 text-sm mt-1">{errors.gender}</div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2">
                      <Field type="radio" name="stsCd" value="A" checked={values.stsCd === "A"} className="text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <Field type="radio" name="stsCd" value="D" checked={values.stsCd === "D"} className="text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700">Deactive</span>
                    </label>
                  </div>
                  {errors.stsCd && touched.stsCd && (
                    <div className="text-red-500 text-sm mt-1">{errors.stsCd}</div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2">
                      <label className="flex items-center space-x-2">
                      <Field type="radio" name="acctType" value="ADMIN" checked={values.acctType === "ADMIN"} className="text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700">Admin</span>
                    </label>
                      <Field type="radio" name="acctType" value="MKTG_EXEC" checked={values.acctType === "MKTG_EXEC"} className="text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700">Marketing Executive</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <Field type="radio" name="acctType" value="RECRUITER" checked={values.acctType === "RECRUITER"} className="text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700">Recruiter</span>
                    </label>
                  </div>
                  {errors.acctType && touched.acctType && (
                    <div className="text-red-500 text-sm mt-1">{errors.acctType}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 mb-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2 mb-3">
                <FaMapMarkerAlt size={18} className="text-[#201F1E]" /> 
                Address Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <TextInput
                    value={values.addressLine1}
                    name="addressLine1"
                    placeholder="Address Line 1"
                    handleChange={handleChange}
                    legend
                    required
                  />
                  {errors.addressLine1 && touched.addressLine1 && (
                    <div className="text-red-500 text-sm mt-1">{errors.addressLine1}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.addressLine2}
                    name="addressLine2"
                    placeholder="Address Line 2"
                    handleChange={handleChange}
                    legend
                  />
                  {errors.addressLine2 && touched.addressLine2 && (
                    <div className="text-red-500 text-sm mt-1">{errors.addressLine2}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.city}
                    name="city"
                    placeholder="City"
                    handleChange={handleChange}
                    legend
                    required
                  />
                  {errors.city && touched.city && (
                    <div className="text-red-500 text-sm mt-1">{errors.city}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.state}
                    name="state"
                    placeholder="State"
                    handleChange={handleChange}
                    legend
                    required
                  />
                  {errors.state && touched.state && (
                    <div className="text-red-500 text-sm mt-1">{errors.state}</div>
                  )}
                </div>
                <div>
                  <CustomDropdown
                    value={countryOptions.find((user) => user?.value === values?.country)}
                    name="country"
                    handleChange={(selectedOption) =>
                      setFieldValue("country", selectedOption.value)
                    }
                    placeholder="Select Country"
                    options={countryOptions}
                  />
                  {errors.country && touched.country && (
                    <div className="text-red-500 text-sm mt-1">{errors.country}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.zip}
                    name="zip"
                    placeholder="Zip Code"
                    handleChange={e => {
                      const allowed = e.target.value.replace(/[^\d]/g, "").slice(0, values.country === "USA" ? 5 : values.country === "IND" ? 6 : 6);
                      setFieldValue("zip", allowed);
                    }}
                    required
                    legend
                    maxLength={values.country === "USA" ? 5 : values.country === "IND" ? 6 : 6}
                  />
                  {((values.country === "USA" && values.zip.length !== 0 && values.zip.length !== 5) || (values.country === "IND" && values.zip.length !== 0 && values.zip.length !== 6)) && (
                    <div className="text-red-500 text-sm mt-1">
                      {values.country === "USA" && values.zip.length !== 5 && "For USA, zip code must be exactly 5 digits."}
                      {values.country === "IND" && values.zip.length !== 6 && "For India, zip code must be exactly 6 digits."}
                    </div>
                  )}
                  {errors.zip && touched.zip && (
                    <div className="text-red-500 text-sm mt-1">{errors.zip}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2  shadow-sm"
                onClick={() => navigate("/users")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-4 py-2 border border-transparent text-white bg-[#201F1E] rounded-md shadow-sm ${
                  isSubmitting
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-[#201F1E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#201F1E]"
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Hoc(AddEmployeeForm);
